import { format, formatDistanceToNow } from 'date-fns';

import { EventIcon } from '@/components/fleet/event-icon';
import { EventSourceBadge } from '@/components/fleet/event-source-badge';
import { EmptyState } from '@/components/fleet/states';
import type { EventType, FleetEvent } from '@/types/fleet';

/** Privileged lifecycle event types surfaced distinctly in the audit log. */
const PRIVILEGED_EVENT_TYPES: ReadonlySet<EventType> = new Set([
  'decommissioned',
  'quarantined',
  'recovered',
]);

/** Reads a string field off an event payload, tolerating missing/typed keys. */
function payloadString(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];

  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/**
 * Surfaces the recent *privileged* lifecycle actions on a server — the
 * decommission / quarantine / recover events emitted by the admin UI. These
 * arrive in the server-detail `events[]` with `source: 'admin-ui'`; this
 * component pulls them out and renders actor + reason from the event payload.
 *
 * The full event timeline still lives in `EventTimeline`; this is the
 * focused "who did the destructive things, and why" audit view.
 */
export function AuditLog({ events }: { events: FleetEvent[] }): JSX.Element {
  const privileged = events
    .filter((e) => PRIVILEGED_EVENT_TYPES.has(e.event_type as EventType))
    .sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime());

  if (privileged.length === 0) {
    return <EmptyState message="No privileged actions recorded for this server" />;
  }

  return (
    <ul className="divide-y divide-border">
      {privileged.map((event) => {
        const occurred = new Date(event.occurred_at);
        // Actor / reason are best-effort: the backend writes them into the
        // vps_events payload, but key names can vary across emitters.
        const actor =
          payloadString(event.payload, 'actor') ??
          payloadString(event.payload, 'triggered_by') ??
          payloadString(event.payload, 'user');
        const reason =
          payloadString(event.payload, 'reason') ??
          payloadString(event.payload, 'decommission_reason');

        return (
          <li key={event.id} className="flex gap-3 py-2.5">
            <div className="mt-0.5">
              <EventIcon eventType={event.event_type} />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium capitalize">
                  {event.event_type.replace('_', ' ')}
                </span>
                <EventSourceBadge source={event.source} />
                <span
                  className="font-mono text-xs text-muted-foreground"
                  title={format(occurred, 'yyyy-MM-dd HH:mm:ss')}
                >
                  {formatDistanceToNow(occurred, { addSuffix: true })}
                </span>
              </div>
              <dl className="grid grid-cols-[auto,1fr] gap-x-3 text-xs">
                <dt className="font-mono uppercase tracking-wide text-muted-foreground">Actor</dt>
                <dd className="font-mono">{actor ?? 'unknown'}</dd>
                {reason && (
                  <>
                    <dt className="font-mono uppercase tracking-wide text-muted-foreground">Reason</dt>
                    <dd>{reason}</dd>
                  </>
                )}
              </dl>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
